import MenuItem from '@mui/material/MenuItem';
import MuiSelect, { type SelectProps as MuiSelectProps } from '@mui/material/Select';
import OutlinedInput from '@mui/material/OutlinedInput';
import type { ReactNode, Ref } from 'react';

import { cssVar } from '../../theme/tokens';
import { type FieldProps, FormField, splitFieldProps, useFormField } from './FormField';

export interface SelectOption<V extends string | number = string> {
  value: V;
  label: ReactNode;
  disabled?: boolean;
}

export interface SelectProps<V extends string | number = string> extends FieldProps {
  options: readonly SelectOption<V>[];
  /** The selected value, or `''` for none (shows the placeholder). */
  value: V | '';
  onChange: (value: V) => void;
  onBlur?: () => void;
  placeholder?: string;
  name?: string;
  /** Reaches the hidden native input (React Hook Form focus). */
  ref?: Ref<HTMLInputElement>;
  /** Extra props for MUI's Select (menu placement, `renderValue`, …). */
  selectProps?: Omit<MuiSelectProps<V | ''>, 'value' | 'onChange' | 'input' | 'labelId'>;
}

function SelectControl<V extends string | number>({
  options,
  value,
  onChange,
  onBlur,
  placeholder,
  name,
  ref,
  selectProps,
}: Omit<SelectProps<V>, keyof FieldProps>) {
  const field = useFormField();
  const labelOf = (selected: V | '') =>
    options.find((option) => option.value === selected)?.label ?? selected;
  return (
    <MuiSelect<V | ''>
      labelId={field.labelId}
      value={value}
      name={name}
      inputRef={ref}
      displayEmpty={placeholder !== undefined}
      onChange={(event) => onChange(event.target.value as V)}
      onBlur={onBlur}
      input={<OutlinedInput />}
      renderValue={(selected) =>
        selected === '' ? (
          <span style={{ color: cssVar('text-subtle') }}>{placeholder}</span>
        ) : (
          labelOf(selected)
        )
      }
      SelectDisplayProps={{ id: field.controlId, 'aria-describedby': field.describedBy }}
      {...selectProps}
    >
      {options.map((option) => (
        <MenuItem key={option.value} value={option.value} disabled={option.disabled}>
          {option.label}
        </MenuItem>
      ))}
    </MuiSelect>
  );
}

/** A single-choice list with label, hint and error, as tall as the other controls of its size. */
export function Select<V extends string | number = string>(props: SelectProps<V>) {
  const { field, control } = splitFieldProps(props);
  return (
    <FormField {...field}>
      <SelectControl<V> {...control} />
    </FormField>
  );
}
